/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../config/api.config';

const StaffContext = createContext(null);

export const StaffProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [staff, setStaff] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      // Use the SAME auth endpoint as superadmin
      const response = await fetch(buildApiUrl('superadmin/api/auth.php') + '?action=check_session', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      // Check if user is staff (admin or support role)
      if (data.success && data.authenticated && data.user) {
        const userRole = data.user.role;
        if (userRole === 'admin' || userRole === 'support') {
          setStaff(data.user);
          setIsAuthenticated(true);
        } else {
          // Not a staff member
          setStaff(null);
          setIsAuthenticated(false);
        }
      } else {
        setStaff(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Session check failed:', error);
      setStaff(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Staff authentication is route-scoped. Public visitors and normal company
  // users should not pay for a staff-session request during initial render.
  useEffect(() => {
    if (!location.pathname.startsWith('/staff')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    checkSession();
  }, [checkSession, location.pathname]);

  const logout = async () => {
    try {
      await fetch(buildApiUrl('superadmin/api/auth.php'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'logout' }),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setStaff(null);
      setIsAuthenticated(false);
      navigate('/login'); // Single login page
    }
  };

  const value = {
    staff,
    isAuthenticated,
    loading,
    logout,
    checkSession
  };

  return (
    <StaffContext.Provider value={value}>
      {children}
    </StaffContext.Provider>
  );
};

export const useStaff = () => {
  const context = useContext(StaffContext);
  if (!context) {
    throw new Error('useStaff must be used within a StaffProvider');
  }
  return context;
};

export default StaffContext;
